import React, { useEffect, Dispatch, SetStateAction } from "react";
import { Modal } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import AddressForm from "./AddressForm";
import { mapLocationToForm, buildPayload } from "./addressMapper";
import { AddressFlowState } from "./useAddressFlow";
import NavigoDashboardUtils from "../../../services/NavigoDashboardUtils";
import AddressService from "../../../services/AddressService";

const navigoDashboardUtils = new NavigoDashboardUtils();
const addressService = new AddressService();

/**
 * coordinates → address object
 */
function getAddressByLatLng({
  lat,
  lng,
}: {
  lat: number;
  lng: number;
}): Promise<any> {
  if (lat == null || lng == null) {
    return Promise.reject("lat/lng required");
  }

  return new Promise((resolve, reject) => {
    navigoDashboardUtils.getAddressByCoordinates(
      { latitude: lat, longitude: lng },
      (res: any) => resolve(res),
      (err: any) => reject(err),
    );
  });
}

interface AddressFlowDialogProps {
  flowState: AddressFlowState;
  setFlowState: Dispatch<SetStateAction<AddressFlowState>>;
  closeFlow: (result: any) => void;
  openLocationDialog: (initialData?: any) => Promise<any>;
  patientId: number;
}

const AddressFlowDialog: React.FC<AddressFlowDialogProps> = ({
  flowState,
  setFlowState,
  closeFlow,
  openLocationDialog,
  patientId,
}) => {
  const { visible, step, locationData, formData, editingAddressId } = flowState;

  // STEP 1 → MAP (for new address or address without coordinates)
  useEffect(() => {
    if (visible && step === "map") {
      (async () => {
        const result = await openLocationDialog(locationData);

        if (!result?.confirmed) {
          closeFlow({ confirmed: false });
          return;
        }

        const mapped = mapLocationToForm(result);

        setFlowState((prev) => ({
          ...prev,
          locationData: result,
          formData: {
            ...prev.formData,
            ...mapped,
            coordinates: {
              latitude: result.latLng.lat,
              longitude: result.latLng.lng,
            },
          },
          step: "form",
        }));
      })();
    }
  }, [visible, step]);

  // FETCH ADDRESS FROM COORDINATES (for display in the Location bar)
  useEffect(() => {
    if (visible && step === "form" && formData?.coordinates) {
      const { latitude, longitude } = formData.coordinates;
      if (latitude && longitude && latitude !== 0 && longitude !== 0) {
        (async () => {
          try {
            const address = await getAddressByLatLng({
              lat: latitude,
              lng: longitude,
            });
            setFlowState((prev) => ({
              ...prev,
              googleAddress: {
                line1: address.building || address.housing?.split(",")[0] || "",
                line2:
                  address.street ||
                  `${address.neighborhood || ""} ${address.subLocality || ""}`.trim() ||
                  "",
                city: address.city || "",
                district: address.district || "",
                state: address.state || "",
                pincode: address.pincode || "",
                landmark: address.landmark || "",
              },
            }));
          } catch (e) {
            console.error("Failed to fetch address:", e);
          }
        })();
      }
    }
  }, [visible, step, formData?.coordinates]);

  // CHANGE LOCATION
  const handleChangeLocation = async () => {
    setFlowState((prev) => ({
      ...prev,
      step: "map",
    }));

    const result = await openLocationDialog(locationData);

    if (!result?.confirmed) {
      setFlowState((prev) => ({
        ...prev,
        step: "form",
      }));
      return;
    }

    const mapped = mapLocationToForm(result);

    setFlowState((prev) => ({
      ...prev,
      locationData: result,
      formData: {
        ...prev.formData,
        ...mapped,
        coordinates: {
          latitude: result.latLng.lat,
          longitude: result.latLng.lng,
        },
      },
      step: "form",
    }));
  };

  // SAVE
  const handleSubmit = () => {
    const requiredFields = [
      { key: "name", label: "Full name" },
      { key: "phoneNo", label: "Mobile number" },
      { key: "line1", label: "House No. / Floor / Building" },
      { key: "line2", label: "Colony / Street / Area" },
      { key: "pincode", label: "Pincode" },
      { key: "city", label: "City" },
      { key: "district", label: "District" },
      { key: "state", label: "State" },
      { key: "type", label: "Address type" },
    ];

    for (const field of requiredFields) {
      if (!formData?.[field.key]?.toString().trim()) {
        showNotification({
          title: `${field.label} is required`,
          message: "",
          color: "yellow",
        });
        return;
      }
    }

    if (
      !formData?.coordinates?.latitude ||
      !formData?.coordinates?.longitude ||
      (formData.coordinates.latitude === 0 &&
        formData.coordinates.longitude === 0)
    ) {
      showNotification({
        title: "Please select a geo location using the Change button",
        message: "",
        color: "yellow",
      });
      return;
    }

    if (!/^\d{10}$/.test(formData.phoneNo?.toString().trim() ?? "")) {
      showNotification({
        title: "Mobile number must be exactly 10 digits",
        message: "",
        color: "yellow",
      });
      return;
    }

    if (!/^\d{6}$/.test(formData.pincode?.toString().trim() ?? "")) {
      showNotification({
        title: "Pincode must be exactly 6 digits",
        message: "",
        color: "yellow",
      });
      return;
    }

    const payload = buildPayload(formData, locationData, patientId);

    const onSuccess = (data: any) => {
      closeFlow({ confirmed: true, data });
    };

    const onError = (err: any) => {
      showNotification({
        title: err?.message || "Something went wrong",
        message: "",
        color: "red",
      });
    };

    if (editingAddressId) {
      addressService.updatePatientAddress(
        editingAddressId,
        patientId,
        payload,
        onSuccess,
        onError,
      );
    } else {
      addressService.addPatientAddress(payload, onSuccess, onError);
    }
  };

  if (!visible || step !== "form") return null;

  return (
    <Modal
      size="lg"
      centered
      style={{ position: "fixed" }}
      opened={visible && step === "form"}
      withCloseButton={false}
      onClose={() => closeFlow({ confirmed: false })}
    >
      <AddressForm
        data={formData || {}}
        googleAddress={flowState.googleAddress}
        onChange={(data: any) =>
          setFlowState((prev) => ({ ...prev, formData: data }))
        }
        onSubmit={handleSubmit}
        onChangeLocation={handleChangeLocation}
        onHide={() => closeFlow({ confirmed: false })}
      />
    </Modal>
  );
};

export default AddressFlowDialog;
